import { useState, useCallback } from "react";
import { analytics } from "@/lib/analytics";

/**
 * Dismissal locations for upgrade CTAs.
 * "become-driver" is the sidebar "Become a Driver" promo (the permanent entry
 * point remains on the Profile page).
 */
export type DismissalLocation = "sidebar" | "rides-banner" | "dashboard" | "become-driver";

/**
 * Get the sessionStorage key for a dismissal location.
 * @param location - The dismissal location
 * @returns The sessionStorage key for this location
 */
function getDismissalKey(location: DismissalLocation): string {
  return `upgrade_dismissed_${location}`;
}

/**
 * Hook to manage session-based dismissal tracking for upgrade CTAs.
 * Dismissals persist for the current browser session only.
 * When the user logs out or closes the browser, dismissals are reset.
 *
 * @param location - The location identifier for the CTA
 * @returns Object with isDismissed state and dismiss function
 */
export function useUpgradeDismissal(location: DismissalLocation): {
  isDismissed: boolean;
  dismiss: () => void;
} {
  const [isDismissed, setIsDismissed] = useState(() => {
    const key = getDismissalKey(location);
    return sessionStorage.getItem(key) === "true";
  });

  const dismiss = useCallback(() => {
    const key = getDismissalKey(location);
    sessionStorage.setItem(key, "true");
    setIsDismissed(true);

    // Track the dismissal in analytics
    analytics.trackCtaDismissed(location, "upgrade");
  }, [location]);

  return { isDismissed, dismiss };
}

/**
 * Clear all upgrade CTA dismissals.
 * Call this on logout to reset dismissal state for the next session.
 */
export function clearUpgradeDismissals(): void {
  const locations: DismissalLocation[] = ["sidebar", "rides-banner", "dashboard", "become-driver"];
  locations.forEach((location) => {
    sessionStorage.removeItem(getDismissalKey(location));
  });
}
